import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import type { RootState, AppDispatch } from 'app/store';
import { fetchSignupAvailability, setJourneyConfig } from 'features/frictionless-signup/reducers/signupSlice';
import { getJourneyFromQueryParam } from 'features/frictionless-signup/config/journeyConfig';
import SignupForm from 'features/frictionless-signup/components/SignupForm';
import SignupSuccess from 'features/frictionless-signup/components/SignupSuccess';
import SignupDisabled from 'features/frictionless-signup/components/SignupDisabled';
import AccountFoundPage from 'features/frictionless-signup/components/AccountFoundPage';
import ContactSupportButton from 'components/ContactSupportButton';
import dataopsLogoBlueSvg from 'assets/logos/dataops/dataops-logo-blue.svg';

const SignupView: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { signupSuccess, accountFound, journeyConfig, signupAvailable } = useSelector(
    (state: RootState) => state.signup,
  );
  const [searchParams] = useSearchParams();

  // Load journey configuration from query params or use default
  // For unauthenticated signup, we allow deep linking via ?app=cicd or ?app=dbt
  // If no query param is provided, we use the default "Onboarding" journey
  useEffect(() => {
    const config = getJourneyFromQueryParam(searchParams);
    dispatch(setJourneyConfig(config));
  }, [searchParams, dispatch]);

  // Probe whether website signups are currently accepted before rendering
  // the form.  Backed by the same PostHog feature flag the backend uses to
  // gate POST /signup, so the UI and the API stay in sync.  See
  // `fetchSignupAvailability` for failure-mode handling (probe failure
  // optimistically allows the form; the POST is the authoritative gate).
  useEffect(() => {
    void dispatch(fetchSignupAvailability());
  }, [dispatch]);

  /**
   * Determine which content to display based on signup state:
   * - Availability not yet known: Render nothing (avoid flashing the form)
   * - Signups disabled by feature flag: Show the disabled screen
   * - Account found: Show account found page with sign-in option
   * - Signup success: Show success page with password setup instructions
   * - Default: Show signup form
   *
   * Note: the disabled / accountFound / success branches are intentionally
   * checked before the availability gate so that, if the flag flips off
   * mid-flow (e.g. between submit-pending and submit-rejected), an
   * already-resolved success or account-found state still wins. The 400
   * race is handled separately by `submitSignup.rejected` flipping
   * `signupAvailable` to false, which falls through to this same branch.
   */
  const renderContent = (): React.ReactElement | null => {
    if (accountFound) {
      return <AccountFoundPage />;
    }
    if (signupSuccess) {
      return <SignupSuccess />;
    }
    if (signupAvailable === null) {
      // Availability probe in flight - render nothing inside the card to
      // avoid flashing the form for a frame.  Outer chrome (logo, title,
      // sign-in link, support button) still renders so the page doesn't
      // look broken.
      return null;
    }
    if (!signupAvailable) {
      return <SignupDisabled />;
    }
    return <SignupForm />;
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img className="mx-auto h-12 w-auto" src={dataopsLogoBlueSvg} alt="DataOps.live" />
        <h2 className="mt-6 text-center text-3xl  text-gray-900">
          {journeyConfig?.title ?? 'Get started with DataOps.live'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {journeyConfig?.description ?? 'Create your account and start automating your data pipelines'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:px-10">{renderContent()}</div>
      </div>

      <div className="mt-4 text-center text-sm text-gray-600">
        Already have a DataOps.live account.{' '}
        <a href="/login" className="font-medium text-blue-600 hover:text-blue-500">
          Click here.
        </a>
      </div>

      <ContactSupportButton />
    </div>
  );
};

export default SignupView;
